import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/hooks/use-auth'
import { messages } from '@/lib/messages'
import { toProblem } from '@/lib/problem'

export function ChangePasswordPage() {
  const { changePassword, isChangingPassword, mustChangePassword } = useAuth()
  const navigate = useNavigate()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    if (!currentPassword) {
      setError(messages.auth.errors.passwordRequired)
      return
    }
    if (newPassword.length < 8) {
      setError(messages.auth.errors.passwordTooShort)
      return
    }
    if (newPassword !== repeat) {
      setError(messages.auth.errors.passwordMismatch)
      return
    }
    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword })
      navigate('/', { replace: true })
    } catch (submitError) {
      setError(toProblem(submitError).detail || messages.errors.unexpected)
    }
  }

  return (
    <div className="bg-muted flex min-h-dvh items-center justify-center p-4">
      <div className="border-border bg-card text-card-foreground w-full max-w-sm rounded-md border p-6">
        <h1 className="text-lg font-semibold">{messages.auth.changePasswordTitle}</h1>
        {mustChangePassword && (
          <p className="text-muted-foreground mt-1 text-sm">{messages.auth.changePasswordForced}</p>
        )}

        <form className="mt-4 flex flex-col gap-3" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="current-password">{messages.auth.currentPassword}</Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-password">{messages.auth.newPassword}</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-password-repeat">{messages.auth.newPasswordRepeat}</Label>
            <Input
              id="new-password-repeat"
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(event) => setRepeat(event.target.value)}
            />
          </div>

          {error && (
            <p role="alert" className="text-destructive text-sm">
              {error}
            </p>
          )}

          <Button type="submit" disabled={isChangingPassword} className="mt-1">
            {isChangingPassword ? messages.common.saving : messages.common.save}
          </Button>
        </form>
      </div>
    </div>
  )
}
